import {
  AfterContentChecked, AfterViewChecked,
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  model,
  ModelSignal,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {ButtonComponent, ButtonOptions, ModalComponent, NavOptions, TableOptions} from '@shared/components';
import { NgxPageScrollDirective } from 'ngx-page-scroll';
import { EasingLogic } from 'ngx-page-scroll-core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {APIService, DataService} from '@core/services';
import {TranslateService, Translation} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AllergiesComponent} from '@modules/cy/patient/ps/components/allergies/allergies.component';
import {VaccinationsComponent} from '@modules/cy/patient/ps/components/vaccinations/vaccinations.component';
import {PastIllnessesComponent} from '@modules/cy/patient/ps/components/past-illnesses/past-illnesses.component';
import {
  SurgicalProceduresComponent
} from '@modules/cy/patient/ps/components/surgical-procedures/surgical-procedures.component';
import {
  ProblemsDiagnosesComponent
} from '@modules/cy/patient/ps/components/problems-diagnoses/problems-diagnoses.component';
import {DevicesImplantsComponent} from '@modules/cy/patient/ps/components/devices-implants/devices-implants.component';
import {TreatmentsComponent} from '@modules/cy/patient/ps/components/treatments/treatments.component';
import {
  AutonomyInvalidityComponent
} from '@modules/cy/patient/ps/components/autonomy-invalidity/autonomy-invalidity.component';
import {SocialHistoryComponent} from '@modules/cy/patient/ps/components/social-history/social-history.component';
import {
  PhysicalFindingsComponent
} from '@modules/cy/patient/ps/components/physical-findings/physical-findings.component';
import {DiagnosticTestsComponent} from '@modules/cy/patient/ps/components/diagnostic-tests/diagnostic-tests.component';
import {PregnanciesComponent} from '@modules/cy/patient/ps/components/pregnancies/pregnancies.component';
import {PatientService} from '@core/services/patient.service';
import {HttpHeaders} from '@angular/common/http';
import {MedicationComponent} from '@modules/cy/patient/ps/components/medication/medication.component';
import {DemographicsComponent} from '@modules/cy/patient/ps/components/demographics/demographics.component';
import {ContactsComponent} from '@modules/cy/patient/ps/components/contacts/contacts.component';
import {InsurancesComponent} from '@modules/cy/patient/ps/components/insurances/insurances.component';
import {Version} from '@core/classes/tables/version';
import {Response} from '@core/classes/other/response';
import {ProgressSpinner} from 'primeng/progressspinner';
import {ButtonTypes} from '@core/enums';
import {Allergy} from '@core/classes/tables/allergy';
import {
  AllergyStatusActionComponent
} from '@modules/cy/patient/ps/components/allergies/actions/allergy-status-action/allergy-status-action.component';
import {PatientEntries} from '@core/classes/other/patient-entries';
import {PatientSession} from '@core/classes/other/patient-session';
import {VersionsComponent} from '@modules/cy/patient/ps/components/versions/versions.component';
import {PROJECTS} from '@core/constants';
import {environment} from '@env/environment';

@UntilDestroy()
@Component({
  selector: 'ncp-patient-summary',
  imports: [
    AllergiesComponent,
    VaccinationsComponent,
    PastIllnessesComponent,
    SurgicalProceduresComponent,
    ProblemsDiagnosesComponent,
    DevicesImplantsComponent,
    TreatmentsComponent,
    AutonomyInvalidityComponent,
    SocialHistoryComponent,
    PhysicalFindingsComponent,
    DiagnosticTestsComponent,
    PregnanciesComponent,
    MedicationComponent,
    DemographicsComponent,
    ContactsComponent,
    InsurancesComponent,
    RouterOutlet,
    ProgressSpinner,
    ButtonComponent,
    VersionsComponent,
  ],
  templateUrl: './patient-summary.component.html',
  styleUrl: './patient-summary.component.scss',
})
export class PatientSummaryComponent implements OnInit, AfterViewInit {
  @ViewChild('parentDiv') public pDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('demographics') public dDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('content_demographics') public cdDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('stickyContentsTable') public sct!: ElementRef<HTMLUListElement>;
  protected sideNavOptions = signal<NavOptions>({});
  private readonly translate = inject(TranslateService);
  protected readonly router = inject(Router);
  protected readonly apiService = inject(APIService);
  protected readonly patientService = inject(PatientService);
  private cdref = inject(ChangeDetectorRef);
  protected translations: Translation = this.translate.instant('patient.summary');
  protected readonly dataService = inject(DataService);
  protected headHeight: ModelSignal<number> = model<number>(0);
  protected headTop: ModelSignal<number> = model<number>(0);
  protected activeSection: string = 'demographics';
  protected clickedSection: boolean = false;
  protected createButtonOptions = signal<ButtonOptions>({});
  protected versions: ModelSignal<Version[]> = model<Version[]>([]);
  protected entries: ModelSignal<PatientEntries> = model<PatientEntries>({});
  protected isInit: ModelSignal<boolean> = model<boolean>(true);
  protected sctStyle: {[key: string]: string} = {};

  @ViewChild('stickyContentsTable') set stickyContentsTable (el: ElementRef<HTMLUListElement>) {
    if(el) {
      //console.log('set sct' + this.headHeight() + this.headTop());
      //console.log('set sct 2 = ' + (this.dataService.getValueByKey('headerHeight') + this.dataService.getValueByKey('headerTop')))
      el.nativeElement.setAttribute('style', 'top: ' + (this.dataService.getValueByKey('headerHeight') + this.dataService.getValueByKey('headerTop')) + 'px;');
      this.sct = el;
    }
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('patient.summary');
    });

    this.createButtonOptions.set(
      new ButtonOptions({
        id: 'create_ps',
        class: 'btn btn-primary createButton',
        text: 'create_ps',
        type: ButtonTypes.button,
        onClick: this.goToCreate
      })
    );

    this.versions.set(this.dataService.getValueByKey('versions'));

    this.dataService.dataVar$.pipe(untilDestroyed(this)).subscribe((dataVar) => {
      this.headHeight.set(dataVar.get('headerHeight'));
      this.headTop.set(dataVar.get('headerTop'));
      this.versions.set(dataVar.get('versions'));
      if(this.sct) {
        //console.log('set 1 sct' + this.headHeight() + this.headTop());
        //console.log('set 1 sct 2 = ' + (this.dataService.getValueByKey('headerHeight') + this.dataService.getValueByKey('headerTop')))
        this.sctStyle['top'] = (this.dataService.getValueByKey('headerHeight') + this.dataService.getValueByKey('headerTop')) + 'px'
        this.sct.nativeElement.setAttribute('style', this.objToStyleString(this.sctStyle));
      }
      this.cdref.detectChanges();
    });

    const routerEvents = this.router.events.subscribe((event) => {

      if (event instanceof NavigationEnd) {
        console.log('NavigationEnd', event.url);
        if(event.url === '/cy/patient/ps/edit' || event.url === '/cy/patient/ps/create') {
          this.isInit.set(true);
          this.makeRequests(-1);
        } else if(event.url === '/cy/patient/ps' || this.router.url === '/ps') {
          this.isInit.set(true);
          this.makeRequests(0);
        } else {
          routerEvents.unsubscribe();
        }
      }

    });

    if(this.router.url === '/cy/patient/ps/create' || this.router.url === '/cy/patient/ps/edit') {
      this.makeRequests(-1);
    } else if(this.router.url === '/cy/patient/ps' || this.router.url === '/ps') {
      this.isInit.set(true);
      this.makeRequests(0);
    }
  }

  ngAfterViewInit(): void {
    if(this.sct) {
      this.sctStyle['top'] = (this.dataService.getValueByKey('headerHeight') + this.dataService.getValueByKey('headerTop')) + 'px'
      this.sct.nativeElement.setAttribute('style', this.objToStyleString(this.sctStyle));
    }
    this.cdref.detectChanges();
  }

  protected objToStyleString = (obj: {[key: string]: string}): string => {
    let result = '';

    Object.keys(obj).forEach((key: string) => {
      if(obj.hasOwnProperty(key)) {
        result += key + ': ' + obj[key] + '; ';
      }
    })

    return result;
  }

  protected makeRequests(status: number = 0): void {
    this.apiService.callMany(
      [
        // {
        //   path: '/get-ps-versioning',
        //   headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        //   name: 'versioning'
        // },
        {
          path: '/get-ps-entries',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            version_id: this.patientService.patient?.version?.new || -1,
            status: status
          },
          name: 'entries'
        },
        {
          path: environment.project === PROJECTS.PROVIDERS ? '/get-all-unkown-values' : '/get-all-unknown-values',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            version_id: this.patientService.patient?.version?.id || -1,
            status: status
          },
          name: 'unknownValues'
        },
      ]
    )?.subscribe((res) => {
      if (res && res['entries'] && res['unknownValues']) {
        this.isInit.set(false);
        let entriesResponse = res['entries'] as Response<PatientEntries>;
        if(entriesResponse && entriesResponse.data && entriesResponse.data.length > 0) {
          this.entries.set(entriesResponse.data[0]);
        }

        // const versions = (res['versioning'] as Response<Version>);
        // if(versions.data?.length && versions.data?.length > 0) {
        //   this.hasVersion.set(true)
        // } else {
        //   this.hasVersion.set(false)
        // }
      }
    });
  }

  goTo = (el: HTMLHeadingElement): void => {
    this.activeSection = el.id;
    this.clickedSection = true;
    //window.location.href = '/cy/patient/ps' + location;
    window.scrollTo(0,  el.offsetTop - this.headHeight() - 10);
  };

  onScroll = (event: any, el: HTMLHeadingElement, content: HTMLDivElement): void => {
    if(
      (el.getBoundingClientRect().top > (this.headHeight() + this.headTop() + 10 - content.getBoundingClientRect().height)) &&
      ((el.getBoundingClientRect().top) <= (this.headHeight() + this.headTop() + 10))) {
      if(!this.clickedSection && event.type === 'scrollend') {
        this.activeSection = el.id;
      }
      if(event.type === 'scrollend') {
        this.clickedSection = false;
      }
    }
  }

  onResize(event: any, el: HTMLDivElement, type?: string): void {
    // console.log(this.sct.nativeElement.getAttribute('style'))
    this.sctStyle['bottom'] = (this.pDiv.nativeElement.getBoundingClientRect().bottom  < window.innerHeight ? (window.innerHeight - this.pDiv.nativeElement.getBoundingClientRect().bottom - 10) : 0) + 'px'
    this.sctStyle['overflow-y'] = 'auto';
    this.sct.nativeElement.setAttribute('style', this.objToStyleString(this.sctStyle)
      // 'bottom: ' +
      // (this.pDiv.nativeElement.getBoundingClientRect().bottom  < window.innerHeight ? (window.innerHeight - this.pDiv.nativeElement.getBoundingClientRect().bottom - 10) : 0) +
      // 'px;' +
      // 'overflow-y: auto;' //+
      // // this.sct.nativeElement.getAttribute('style')
    );
  }

  goToCreate = () => {
    if(this.patientService.version?.new === -1) {
      this.apiService.call({
        path: '/insert-newversion',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || '')
      })?.subscribe((res) => {
        if (res.status === 1) {
          let patient = JSON.parse(sessionStorage.getItem('patient')!) as PatientSession;
          if (patient && patient.version && patient.version.new && patient.version.id) {
            patient.version.new = res.new_version;
            patient.version.id = -1;
            sessionStorage.setItem('patient', JSON.stringify(patient));
            this.router.navigate(['cy', 'patient', 'ps', 'create']);
          }
        }
      });
    }
    this.router.navigate(['cy', 'patient', 'ps', 'create']);
  }
}
